import { useEffect, useRef, useState } from "react";
import { api, openProtectedFile } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Modal from "@/components/Modal";
import { Plus, Edit2, Trash2, Package, FileText, Upload, Loader2, Search } from "lucide-react";
import { toast } from "sonner";

const UNITS = ["reams", "boxes", "rolls", "kg", "MT", "sheets", "units", "pcs"];
const EMPTY = { name: "", description: "", default_unit: "reams" };

export default function Products() {
  const { isAdmin, user } = useAuth();
  const canEdit = !!user;
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [modal, setModal] = useState(null); // 'add' | 'edit'
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [uploadingId, setUploadingId] = useState(null);
  const [modalFile, setModalFile] = useState(null); // PDF chosen inside the add/edit modal
  const [saving, setSaving] = useState(false);
  const fileRefs = useRef({});
  const modalFileRef = useRef(null);

  const onUploadSpec = async (productId, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingId(productId);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/products/${productId}/spec`, fd);
      toast.success("Specification uploaded");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingId(null);
      if (fileRefs.current[productId]) fileRefs.current[productId].value = "";
    }
  };

  const load = () => api.get("/products").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openAdd = () => { setForm(EMPTY); setEditing(null); setModalFile(null); setModal("add"); };
  const openEdit = (p) => { setForm({ name: p.name, description: p.description, default_unit: p.default_unit }); setEditing(p); setModalFile(null); setModal("edit"); };

  const closeModal = () => {
    setModal(null); setModalFile(null);
    if (modalFileRef.current) modalFileRef.current.value = "";
  };

  const onPickModalFile = (e) => {
    const file = e.target.files?.[0] || null;
    if (file && file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Specification must be a PDF file.");
      e.target.value = "";
      return;
    }
    setModalFile(file);
  };

  const save = async () => {
    if (!form.name.trim() || saving) return;
    setSaving(true);
    try {
      let product;
      if (editing) product = (await api.patch(`/products/${editing.id}`, form)).data;
      else product = (await api.post("/products", form)).data;
      if (modalFile && product?.id) {
        const fd = new FormData();
        fd.append("file", modalFile);
        await api.post(`/products/${product.id}/spec`, fd);
      }
      toast.success(editing ? "Product updated" : "Product added");
      closeModal(); load();
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "Admins only" : (e?.response?.data?.detail || "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success("Deleted");
      setSelected((s) => s.filter((x) => x !== id));
      load();
    } catch { toast.error("Admins only"); }
  };

  const filtered = items.filter((p) =>
    [p.name, p.description, p.default_unit].some((v) => v?.toLowerCase().includes(search.toLowerCase()))
  );

  const filteredIds = filtered.map((p) => p.id);
  const allFilteredSelected = filteredIds.length > 0 && filteredIds.every((id) => selected.includes(id));
  const toggleSelect = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const toggleSelectFiltered = () => setSelected((s) =>
    allFilteredSelected ? s.filter((id) => !filteredIds.includes(id)) : [...new Set([...s, ...filteredIds])]
  );

  const onBulkDelete = async () => {
    if (selected.length === 0) return;
    if (!window.confirm(`Delete ${selected.length} product${selected.length > 1 ? "s" : ""}?`)) return;
    try {
      await api.post("/products/bulk-delete", { ids: selected });
      toast.success(`Deleted ${selected.length} product${selected.length > 1 ? "s" : ""}`);
      setSelected([]);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete products");
    }
  };

  return (
    <div data-testid="products-page">
      {isAdmin && selected.length > 0 && (
        <div className="flex items-center gap-3 mb-4" style={{ padding: "10px 14px", background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: 8 }} data-testid="bulk-actions">
          <span style={{ fontSize: 13, fontWeight: 600 }}>{selected.length} selected</span>
          <button onClick={() => setSelected([])} className="btn-ghost" style={{ fontSize: 12 }}>Clear</button>
          <button onClick={onBulkDelete} className="btn-outline ml-auto" style={{ color: "var(--danger)", borderColor: "var(--danger)" }} data-testid="bulk-delete-btn">
            <Trash2 size={14} /> Delete selected
          </button>
        </div>
      )}
      <div className="flex flex-wrap gap-3 items-center mb-4">
        <div style={{ position: "relative", flex: "1 1 280px", maxWidth: 420 }}>
          <Search size={15} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-2)" }} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search products…" className="input-base" style={{ paddingLeft: 36 }} data-testid="products-search" />
        </div>
        {canEdit && (
          <button onClick={openAdd} className="btn-primary ml-auto" data-testid="add-product-btn">
            <Plus size={15} /> Add Product
          </button>
        )}
      </div>
      {items.length === 0 ? (
        <div className="card-ledger" style={{ padding: 48, textAlign: "center", color: "var(--text-2)" }}>
          <Package size={28} style={{ margin: "0 auto 12px" }} /> No products yet.
        </div>
      ) : filtered.length === 0 ? (
        <div className="card-ledger" style={{ padding: 48, textAlign: "center", color: "var(--text-2)" }} data-testid="products-empty">
          <Package size={28} style={{ margin: "0 auto 12px" }} />
          <div style={{ fontSize: 14 }}>No products match your search.</div>
        </div>
      ) : (
        <div className="card-ledger overflow-hidden">
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr style={{ background: "var(--surface-2)" }}>
              {isAdmin && (
                <th style={{ padding: "12px 16px", textAlign: "left", borderBottom: "1px solid var(--border)", width: 40 }}>
                  <input type="checkbox" checked={allFilteredSelected} onChange={toggleSelectFiltered} title="Select all visible" data-testid="select-all-checkbox" style={{ cursor: "pointer" }} />
                </th>
              )}
              {["Name", "Description", "Default Unit", "Specification", ""].map((h, i) => (
                <th key={i} className="overline" style={{ fontSize: 10, padding: "12px 16px", textAlign: "left", borderBottom: "1px solid var(--border)" }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} style={{ borderBottom: "1px solid var(--border)" }} data-testid={`product-row-${p.id}`}>
                  {isAdmin && (
                    <td style={{ padding: "14px 16px" }}>
                      <input type="checkbox" checked={selected.includes(p.id)} onChange={() => toggleSelect(p.id)} data-testid={`select-checkbox-${p.id}`} style={{ cursor: "pointer" }} />
                    </td>
                  )}
                  <td style={{ padding: "14px 16px", fontWeight: 600, fontSize: 14 }}>{p.name}</td>
                  <td style={{ padding: "14px 16px", fontSize: 13, color: "var(--text-2)" }}>{p.description || "—"}</td>
                  <td style={{ padding: "14px 16px", fontSize: 13 }} className="font-mono-data">{p.default_unit}</td>
                  <td style={{ padding: "14px 16px", fontSize: 13 }}>
                    <div className="flex items-center gap-2">
                      {p.spec_pdf_url ? (
                        <button
                          type="button"
                          onClick={() => openProtectedFile(p.spec_pdf_url).catch(() => toast.error("Could not open PDF"))}
                          className="flex items-center gap-1 btn-ghost"
                          style={{ color: "var(--primary)", padding: 0, background: "none", border: "none", cursor: "pointer" }}
                          data-testid={`product-spec-view-${p.id}`}
                        >
                          <FileText size={14} /> View PDF
                        </button>
                      ) : (
                        <span style={{ color: "var(--text-2)" }}>—</span>
                      )}
                      {canEdit && (
                        <>
                          <input ref={(el) => (fileRefs.current[p.id] = el)} type="file" accept="application/pdf" onChange={(e) => onUploadSpec(p.id, e)} style={{ display: "none" }} data-testid={`product-spec-input-${p.id}`} />
                          <button onClick={() => fileRefs.current[p.id]?.click()} disabled={uploadingId === p.id} className="btn-ghost" style={{ padding: 4 }} title={p.spec_pdf_url ? "Replace PDF" : "Upload PDF"} data-testid={`product-spec-upload-${p.id}`}>
                            {uploadingId === p.id ? <Loader2 size={14} className="spin" /> : <Upload size={14} />}
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: "14px 16px", textAlign: "right" }}>
                    <div className="flex gap-1 justify-end">
                      {canEdit && <button onClick={() => openEdit(p)} className="btn-ghost" title="Edit"><Edit2 size={14} /></button>}
                      {isAdmin && <button onClick={() => remove(p.id)} className="btn-ghost" style={{ color: "var(--danger)" }} title="Delete"><Trash2 size={14} /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <Modal title={editing ? "Edit Product" : "Add Product"} onClose={closeModal} size="md">
          <div className="grid gap-4">
            <div>
              <label className="overline" style={{ fontSize: 10, display: "block", marginBottom: 6 }}>Name <span style={{ color: "var(--primary)" }}>*</span></label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-base" data-testid="product-name" />
            </div>
            <div>
              <label className="overline" style={{ fontSize: 10, display: "block", marginBottom: 6 }}>Description</label>
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="input-base" style={{ fontFamily: "inherit" }} />
            </div>
            <div>
              <label className="overline" style={{ fontSize: 10, display: "block", marginBottom: 6 }}>Default Unit</label>
              <select value={form.default_unit} onChange={(e) => setForm({ ...form, default_unit: e.target.value })} className="input-base">
                {UNITS.map((u) => <option key={u}>{u}</option>)}
              </select>
            </div>
            <div>
              <label className="overline" style={{ fontSize: 10, display: "block", marginBottom: 6 }}>Specification PDF</label>
              <input ref={modalFileRef} type="file" accept="application/pdf" onChange={onPickModalFile} style={{ display: "none" }} data-testid="product-spec-file" />
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => modalFileRef.current?.click()} className="btn-outline" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <Upload size={14} /> {modalFile ? "Change PDF" : "Choose PDF"}
                </button>
                <span style={{ fontSize: 13, color: "var(--text-2)" }} className="truncate">
                  {modalFile ? modalFile.name : (editing?.spec_pdf_name || "No file selected")}
                </span>
              </div>
            </div>
            <div className="flex gap-3 justify-end mt-2">
              <button onClick={closeModal} className="btn-outline" disabled={saving}>Cancel</button>
              <button onClick={save} className="btn-primary" disabled={saving} data-testid="product-save">
                {saving ? <Loader2 size={14} className="spin" /> : null} Save
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
